import {
  DisplayStyleType,
  FilterWidgetChartType,
  LENGTH_RULE_KEY,
  DetailParam,
  TextAlignType,
  WidgetChartType,
  WidgetColorOption,
  WidgetDataType,
  WidgetModeType,
  WidgetSeriesType,
  WidgetTitleStyle,
  WidgetType,
} from '@/dashboard/shared/types/widgets.types';
import { TargetType } from '@/common/types/targetType.types';
import { BY_TARGET_SERIES_TYPE } from '@/common/define/widget.define';
import { SelectboxItem } from '@/common/utils/types';
import { defineAsyncComponent } from 'vue';
import { i18n } from '@/common/locale';

import { QUERY_TYPE } from '@/common/utils/define';
import { DetailType, TOTAL_DETAIL } from '@/common/stores/slide-detail.logic';
import { IconType } from '@/types/icon.types';

export type RequestKeyType =
  | 'metricRequests'
  | 'currentRequests'
  | 'tableRequests'
  | 'scatterRequests'
  | 'topologyRequests'
  | 'heatmapRequests'
  | 'elasticMetricRequests'
  | 'elasticCurrentRequests'
  | 'elasticTableRequests'
  | 'dataClusterRequests';

export const DEFAULT_DASHBOARD_GROUP_ID = {
  ALL: 'all',
  FAVORITE: 'favorite',
  SHARED: 'shared',
  FEATURED_DASHBOARD: 'featuredDashboard',
  FEATURED_REPORT: 'featuredReport',
  CUSTOM_REPORT: 'customReport',
} as const;

export const DEFAULT_DASHBOARD_GROUP = [
  {
    dashboardGroupId: DEFAULT_DASHBOARD_GROUP_ID.ALL,
    dashboardGroupName: 'All',
  },
  {
    dashboardGroupId: DEFAULT_DASHBOARD_GROUP_ID.FAVORITE,
    dashboardGroupName: 'Favorite',
  },
  {
    dashboardGroupId: DEFAULT_DASHBOARD_GROUP_ID.SHARED,
    dashboardGroupName: 'Shared',
  },
  {
    dashboardGroupId: DEFAULT_DASHBOARD_GROUP_ID.FEATURED_DASHBOARD,
    dashboardGroupName: 'Featured Dashboard',
  },
  {
    dashboardGroupId: DEFAULT_DASHBOARD_GROUP_ID.FEATURED_REPORT,
    dashboardGroupName: 'Featured Report',
  },
  {
    dashboardGroupId: DEFAULT_DASHBOARD_GROUP_ID.CUSTOM_REPORT,
    dashboardGroupName: 'Report',
  },
];

export const WIDGET_TYPE = [
  'GRAPHS',
  'ARCHITECTURE',
  'LIST',
  'ALERT',
  'MEMO',
  'TEXT',
  'IMAGE',
  'GROUP',
  'SERVICE',
  'ETC',
  'FILTER',
] as const;

export const MAX_WIDGET_ID_LENGTH = 255;
export const MAX_WIDGET_NAME_LENGTH = 50;

export const WIDGET_TIME_PERIOD = {
  p5m: { display: 'Past 5 Minutes' },
  p10m: { display: 'Past 10 Minutes' },
  p30m: { display: 'Past 30 Minutes' },
  p1h: { display: 'Past 1 Hours' },
  p3h: { display: 'Past 3 Hours' },
  p6h: { display: 'Past 6 Hours' },
  p12h: { display: 'Past 12 Hours' },
  p1d: { display: 'Past 1 Day' },
} as const;

export const TIME_PERIOD_LIST: SelectboxItem[] = [
  {
    name: '$Global Time',
    value: 'globalTime',
  },
  ...Object.keys(WIDGET_TIME_PERIOD).map((key) => ({
    name: WIDGET_TIME_PERIOD[key].display,
    value: key,
  })),
  {
    name: 'WORD.SELECT_FROM_CALENDAR',
    value: 'calendar',
  },
];

export const SCATTER_TIME_PERIOD_LIST = ['p5m', 'p10m'] as const;

// Long-Term 디스플레이 스타일 전용 Time Period 목록 (최대 60분)
export const SCATTER_LONG_TERM_TIME_PERIOD_LIST = ['p10m', 'p30m', 'p1h'] as const;

export const WIDGET_MODE_TYPE = {
  VIEW: 'view',
  EDIT: 'edit',
  PREVIEW: 'preview',
} satisfies Record<string, WidgetModeType>;

export const DEFAULT_WIDGET_TITLE_STYLE: WidgetTitleStyle = {
  fontSize: '15px',
  color: 'var(--main-font-color)',
  textAlign: 'left',
} as const;

export const DEFAULT_WIDGET_COLOR_OPTION: WidgetColorOption = {
  background: 'var(--widget-frame-default-bg-color)',
  border: 'var(--widget-frame-default-border-color)',
} as const;

export const WIDGET_TYPE_KEY: Record<WidgetType, WidgetType> = {
  GRAPHS: 'GRAPHS',
  LIST: 'LIST',
  ARCHITECTURE: 'ARCHITECTURE',
  ALERT: 'ALERT',
  SERVICE: 'SERVICE',
  ETC: 'ETC',
  MEMO: 'MEMO',
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  GROUP: 'GROUP',
  FILTER: 'FILTER',
};

export const AllWidgetTypes = Object.values(WIDGET_TYPE_KEY);

export const WIDGET_CHART_TYPE_KEY: Record<WidgetChartType, WidgetChartType> = {
  TIME_SERIES: 'TIME_SERIES',
  SCATTER: 'SCATTER',
  ONE_DAY_COMPARE: 'ONE_DAY_COMPARE',
  EQUALIZER: 'EQUALIZER',
  SCOREBOARD: 'SCOREBOARD',
  PIE: 'PIE',
  STACKED_PROPORTION_BAR: 'STACKED_PROPORTION_BAR',
  GAUGE: 'GAUGE',
  ACTION_VIEW: 'ACTION_VIEW',
  TABLE: 'TABLE',
  TOP_LIST: 'TOP_LIST',
  TREE_GRID: 'TREE_GRID',
  // CLUSTER_OVERVIEW: 'CLUSTER_OVERVIEW',
  TOPOLOGY: 'TOPOLOGY',
  EXCLUSIVE: 'EXCLUSIVE',
  STATUS: 'STATUS',
  STATUS_SUMMARY: 'STATUS_SUMMARY',
  SERVICE_CARD: 'SERVICE_CARD',
  MEMO: 'MEMO',
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  GROUP: 'GROUP',
  TAB_GROUP: 'TAB_GROUP',
  HORIZONTAL_FILTER: 'HORIZONTAL_FILTER',
  VERTICAL_FILTER: 'VERTICAL_FILTER',
  SERVER: 'SERVER',
  LEGEND: 'LEGEND',
  HEATMAP: 'HEATMAP',
  CLUSTER: 'CLUSTER',
  REALTIME: 'REALTIME',
  BY_PERIOD: 'BY_PERIOD',
};

export const CHART_TYPE_TO_REQUEST_TYPE_MAP: Record<
  Exclude<
    RequestKeyType,
    | 'elasticMetricRequests'
    | 'elasticScatterRequests'
    | 'elasticCurrentRequests'
    | 'elasticTableRequests'
  >,
  WidgetChartType[]
> = {
  metricRequests: [WIDGET_CHART_TYPE_KEY.TIME_SERIES, WIDGET_CHART_TYPE_KEY.ONE_DAY_COMPARE],
  scatterRequests: [WIDGET_CHART_TYPE_KEY.SCATTER],
  currentRequests: [
    WIDGET_CHART_TYPE_KEY.SCOREBOARD,
    WIDGET_CHART_TYPE_KEY.STACKED_PROPORTION_BAR,
    WIDGET_CHART_TYPE_KEY.GAUGE,
    WIDGET_CHART_TYPE_KEY.EXCLUSIVE,
  ],
  tableRequests: [
    WIDGET_CHART_TYPE_KEY.EQUALIZER,
    WIDGET_CHART_TYPE_KEY.PIE,
    WIDGET_CHART_TYPE_KEY.ACTION_VIEW,
    WIDGET_CHART_TYPE_KEY.TREE_GRID,
    WIDGET_CHART_TYPE_KEY.TOP_LIST,
    WIDGET_CHART_TYPE_KEY.STATUS,
    WIDGET_CHART_TYPE_KEY.STATUS_SUMMARY,
    WIDGET_CHART_TYPE_KEY.SERVER,
    WIDGET_CHART_TYPE_KEY.SERVICE_CARD,
    WIDGET_CHART_TYPE_KEY.REALTIME,
    WIDGET_CHART_TYPE_KEY.BY_PERIOD,
  ],
  topologyRequests: [WIDGET_CHART_TYPE_KEY.TOPOLOGY],
  heatmapRequests: [WIDGET_CHART_TYPE_KEY.HEATMAP],
  dataClusterRequests: [WIDGET_CHART_TYPE_KEY.CLUSTER],
};

export const validStatByChartType: Partial<Record<WidgetChartType, string[]>> = {
  [WIDGET_CHART_TYPE_KEY.ACTION_VIEW]: ['app_activity_monitor', 'ui_intermax_activity'],
};

export const DISPLAY_STYLE_KEY: Record<DisplayStyleType, DisplayStyleType> = {
  LINE: 'LINE',
  AREA: 'AREA',
  BAR: 'BAR',
  STACKED_AREA: 'STACKED_AREA',
  STACKED_BAR: 'STACKED_BAR',
  SINGLE_STACK: 'SINGLE_STACK',
  MULTI_STACK: 'MULTI_STACK',
  STYLE_1: 'STYLE_1',
  STYLE_2: 'STYLE_2',
  STYLE_3: 'STYLE_3',
  HEXA: 'HEXA',
  CARD: 'CARD',
  TABLE: 'TABLE',
  TOP_3_CARD: 'TOP_3_CARD',
  SUMMARY_BAR: 'SUMMARY_BAR',
  SUMMARY_CARD: 'SUMMARY_CARD',
  PIE: 'PIE',
  TREE_GRID: 'TREE_GRID',
  TOPOLOGY: 'TOPOLOGY',
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL',
  SERVER: 'SERVER',
  GAUGE_HALF_CIRCLE: 'GAUGE_HALF_CIRCLE',
  GAUGE_CIRCLE: 'GAUGE_CIRCLE',
  CURRENT_HEATMAP: 'CURRENT_HEATMAP',
  CLUSTER: 'CLUSTER',
  POINT_MAP: 'POINT_MAP',
  EQUALIZER_GROUP: 'EQUALIZER_GROUP',
  EQUALIZER_ENTRY: 'EQUALIZER_ENTRY',
  CARD_LIST: 'CARD_LIST',
  SCOREBOARD: 'SCOREBOARD',
  SCOREBOARD_TABLE: 'SCOREBOARD_TABLE',
  ALERT_TABLE: 'ALERT_TABLE',
  SCATTER_BASIC: 'SCATTER_BASIC',
  SCATTER_LONG_TERM: 'SCATTER_LONG_TERM',
  '': '',
};

export const getWidgetTypeList = (): { name: string; value: WidgetType }[] => [
  { name: i18n.global.t('WORD.CREATE_GROUP_WIDGET'), value: WIDGET_TYPE_KEY.GROUP },
  { name: i18n.global.t('WORD.GRAPHS'), value: WIDGET_TYPE_KEY.GRAPHS },
  { name: i18n.global.t('WORD.LIST'), value: WIDGET_TYPE_KEY.LIST },
  { name: i18n.global.t('WORD.ARCHITECTURE'), value: WIDGET_TYPE_KEY.ARCHITECTURE },
  { name: i18n.global.t('WORD.ALERT'), value: WIDGET_TYPE_KEY.ALERT },
  { name: i18n.global.t('WORD.BUSINESS'), value: WIDGET_TYPE_KEY.SERVICE },
  { name: i18n.global.t('WORD.ETC'), value: WIDGET_TYPE_KEY.ETC },
  { name: i18n.global.t('WORD.FILTER'), value: WIDGET_TYPE_KEY.FILTER },
];

export const getWidgetChartTypeList = (): Record<
  WidgetType,
  { name: string; value: WidgetChartType }[]
> => {
  return {
    GRAPHS: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TIME_SERIES'),
        value: WIDGET_CHART_TYPE_KEY.TIME_SERIES,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SCATTER'),
        value: WIDGET_CHART_TYPE_KEY.SCATTER,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.ONE_DAY_COMPARE'),
        value: WIDGET_CHART_TYPE_KEY.ONE_DAY_COMPARE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.EQUALIZER'),
        value: WIDGET_CHART_TYPE_KEY.EQUALIZER,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SCOREBOARD'),
        value: WIDGET_CHART_TYPE_KEY.SCOREBOARD,
      },
      { name: i18n.global.t('WORD.UI.DASHBOARD.PIE'), value: WIDGET_CHART_TYPE_KEY.PIE },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STACKED_PROPORTION_BAR'),
        value: WIDGET_CHART_TYPE_KEY.STACKED_PROPORTION_BAR,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.GAUGE'),
        value: WIDGET_CHART_TYPE_KEY.GAUGE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.ACTION_VIEW'),
        value: WIDGET_CHART_TYPE_KEY.ACTION_VIEW,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.HEATMAP'),
        value: WIDGET_CHART_TYPE_KEY.HEATMAP,
      },
    ],
    LIST: [
      // { name: i18n.global.t('WORD.UI.DASHBOARD.TABLE'), value: WIDGET_CHART_TYPE_KEY.TABLE },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TREE_GRID'),
        value: WIDGET_CHART_TYPE_KEY.TREE_GRID,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TOP_LIST'),
        value: WIDGET_CHART_TYPE_KEY.TOP_LIST,
      },
    ],
    ARCHITECTURE: [
      // { name: 'WORD.UI.DASHBOARD.CLUSTER_OVERVIEW', value: 'CLUSTER_OVERVIEW' },
      // { name: 'WORD.UI.DASHBOARD.TOPOLOGY', value: 'TOPOLOGY' },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.EXCLUSIVE'),
        value: WIDGET_CHART_TYPE_KEY.EXCLUSIVE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STATUS'),
        value: WIDGET_CHART_TYPE_KEY.STATUS,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STATUS_SUMMARY'),
        value: WIDGET_CHART_TYPE_KEY.STATUS_SUMMARY,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TOPOLOGY'),
        value: WIDGET_CHART_TYPE_KEY.TOPOLOGY,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SERVER'),
        value: WIDGET_CHART_TYPE_KEY.SERVER,
      },
      {
        name: i18n.global.t('WORD.CLUSTER'),
        value: WIDGET_CHART_TYPE_KEY.CLUSTER,
      },
    ],
    ALERT: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.REALTIME'),
        value: WIDGET_CHART_TYPE_KEY.REALTIME,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.BY_PERIOD'),
        value: WIDGET_CHART_TYPE_KEY.BY_PERIOD,
      },
    ],
    SERVICE: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.CARD'),
        value: WIDGET_CHART_TYPE_KEY.SERVICE_CARD,
      },
    ],
    ETC: [
      { name: i18n.global.t('WORD.UI.DASHBOARD.MEMO'), value: WIDGET_CHART_TYPE_KEY.MEMO },
      { name: i18n.global.t('WORD.UI.DASHBOARD.TEXT'), value: WIDGET_CHART_TYPE_KEY.TEXT },
      { name: i18n.global.t('WORD.UI.DASHBOARD.IMAGE'), value: WIDGET_CHART_TYPE_KEY.IMAGE },
    ],
    MEMO: [{ name: i18n.global.t('WORD.UI.DASHBOARD.MEMO'), value: WIDGET_CHART_TYPE_KEY.MEMO }],
    TEXT: [{ name: i18n.global.t('WORD.UI.DASHBOARD.TEXT'), value: WIDGET_CHART_TYPE_KEY.TEXT }],
    IMAGE: [{ name: i18n.global.t('WORD.UI.DASHBOARD.IMAGE'), value: WIDGET_CHART_TYPE_KEY.IMAGE }],
    GROUP: [
      { name: i18n.global.t('WORD.UI.DASHBOARD.GROUP'), value: WIDGET_CHART_TYPE_KEY.GROUP },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TAB_GROUP'),
        value: WIDGET_CHART_TYPE_KEY.TAB_GROUP,
      },
    ],
    FILTER: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.HORIZONTAL_FILTER'),
        value: WIDGET_CHART_TYPE_KEY.HORIZONTAL_FILTER,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.VERTICAL_FILTER'),
        value: WIDGET_CHART_TYPE_KEY.VERTICAL_FILTER,
      },
    ],
  };
};

export const getChartTypeDescription = (): Record<
  Exclude<WidgetChartType, FilterWidgetChartType>,
  string
> => {
  return {
    TIME_SERIES: i18n.global.t('MESSAGE.UI.DASHBOARD.TIME_SERIES'),
    SCATTER: i18n.global.t('MESSAGE.UI.DASHBOARD.SCATTER'),
    ONE_DAY_COMPARE: i18n.global.t('MESSAGE.UI.DASHBOARD.ONE_DAY_COMPARE'),
    EQUALIZER: i18n.global.t('MESSAGE.UI.DASHBOARD.EQUALIZER'),
    SCOREBOARD: i18n.global.t('MESSAGE.UI.DASHBOARD.SCOREBOARD'),
    GAUGE: i18n.global.t('MESSAGE.UI.DASHBOARD.GAUGE'),
    PIE: i18n.global.t('MESSAGE.UI.DASHBOARD.PIE'),
    STACKED_PROPORTION_BAR: '',
    ACTION_VIEW: i18n.global.t('MESSAGE.UI.DASHBOARD.ACTION_VIEW'),
    HEATMAP: i18n.global.t('MESSAGE.UI.DASHBOARD.HEATMAP'),
    TABLE: i18n.global.t('MESSAGE.UI.DASHBOARD.TABLE'),
    TOP_LIST: i18n.global.t('MESSAGE.UI.DASHBOARD.TOP_LIST'),
    TREE_GRID: i18n.global.t('MESSAGE.UI.DASHBOARD.TREE_GRID'),
    // CLUSTER_OVERVIEW: i18n.global.t('DESC.UI.CLUSTER_OVERVIEW_CHART'),
    TOPOLOGY: '',
    EXCLUSIVE: '',
    STATUS: i18n.global.t('MESSAGE.UI.DASHBOARD.STATUS'),
    STATUS_SUMMARY: i18n.global.t('MESSAGE.UI.DASHBOARD.STATUS_SUMMARY'),
    SERVICE_CARD: i18n.global.t('MESSAGE.UI.DASHBOARD.SERVICE_CARD'),
    MEMO: '',
    TEXT: '',
    GROUP: '',
    TAB_GROUP: '',
    IMAGE: '',
    SERVER: i18n.global.t('MESSAGE.UI.DASHBOARD.SERVER'),
    LEGEND: '',
    CLUSTER: i18n.global.t('MESSAGE.UI.DASHBOARD.CLUSTER_CHART'),
    REALTIME: i18n.global.t('MESSAGE.UI.DASHBOARD.REALTIME'),
    BY_PERIOD: i18n.global.t('MESSAGE.UI.DASHBOARD.BY_PERIOD'),
  };
};

export const getDisplayStyleDescription = (): Record<string, string> => {
  return {
    TIME_SERIES_LINE: i18n.global.t('MESSAGE.UI.DASHBOARD.TIME_SERIES_LINE'),
    TIME_SERIES_AREA: i18n.global.t('MESSAGE.UI.DASHBOARD.TIME_SERIES_AREA'),
    TIME_SERIES_BAR: i18n.global.t('MESSAGE.UI.DASHBOARD.TIME_SERIES_BAR'),
    TIME_SERIES_STACKED_AREA: i18n.global.t('MESSAGE.UI.DASHBOARD.TIME_SERIES_STACKED_AREA'),
    TIME_SERIES_STACKED_BAR: i18n.global.t('MESSAGE.UI.DASHBOARD.TIME_SERIES_STACKED_BAR'),
    SCATTER_SCATTER_BASIC: i18n.global.t('MESSAGE.UI.DASHBOARD.SCATTER_BASIC'),
    SCATTER_SCATTER_LONG_TERM: i18n.global.t('MESSAGE.UI.DASHBOARD.SCATTER_LONG_TERM'),
    ONE_DAY_COMPARE_LINE: i18n.global.t('MESSAGE.UI.DASHBOARD.ONE_DAY_COMPARE_LINE'),
    ONE_DAY_COMPARE_BAR: i18n.global.t('MESSAGE.UI.DASHBOARD.ONE_DAY_COMPARE_BAR'),
    EQUALIZER_EQUALIZER_ENTRY: i18n.global.t('MESSAGE.UI.DASHBOARD.EQUALIZER_ENTRY_DESC'),
    EQUALIZER_EQUALIZER_GROUP: i18n.global.t('MESSAGE.UI.DASHBOARD.EQUALIZER_GROUP_DESC'),
    SCOREBOARD_STYLE_1: i18n.global.t('MESSAGE.UI.DASHBOARD.SCOREBOARD_STYLE_1'),
    SCOREBOARD_STYLE_2: i18n.global.t('MESSAGE.UI.DASHBOARD.SCOREBOARD_STYLE_2'),
    STACKED_PROPORTION_BAR_SINGLE_STACK: i18n.global.t(
      'MESSAGE.UI.DASHBOARD.STACKED_BAR_SINGLE_STACK',
    ),
    STACKED_PROPORTION_BAR_MULTI_STACK: i18n.global.t(
      'MESSAGE.UI.DASHBOARD.STACKED_BAR_MULTI_STACK',
    ),
    ACTION_VIEW_STYLE_1: i18n.global.t('MESSAGE.UI.DASHBOARD.ACTION_VIEW_STYLE_1'),
    ACTION_VIEW_STYLE_2: i18n.global.t('MESSAGE.UI.DASHBOARD.ACTION_VIEW_STYLE_2'),
    ACTION_VIEW_STYLE_3: i18n.global.t('MESSAGE.UI.DASHBOARD.ACTION_VIEW_STYLE_3'),
    EXCLUSIVE_STYLE_1: i18n.global.t('MESSAGE.UI.DASHBOARD.EXCLUSIVE_STYLE_1'),
    EXCLUSIVE_STYLE_2: i18n.global.t('MESSAGE.UI.DASHBOARD.EXCLUSIVE_STYLE_2'),
    EXCLUSIVE_STYLE_3: i18n.global.t('MESSAGE.UI.DASHBOARD.EXCLUSIVE_STYLE_3'),
    TOPOLOGY_TOPOLOGY: i18n.global.t('MESSAGE.UI.DASHBOARD.TOPOLOGY'),
    TOPOLOGY_POINT_MAP: i18n.global.t('MESSAGE.UI.DASHBOARD.POINT_MAP'),
    STATUS_HEXA: i18n.global.t('MESSAGE.UI.DASHBOARD.STATUS_HEXA'),
    STATUS_SUMMARY_CARD: i18n.global.t('MESSAGE.UI.DASHBOARD.STATUS_SUMMARY_CARD'),
    TABLE_TOP_3_CARD: i18n.global.t('MESSAGE.UI.DASHBOARD.TABLE_TOP_3_CARD'),
    TOP_LIST_SUMMARY_BAR: i18n.global.t('MESSAGE.UI.DASHBOARD.TOP_LIST_SUMMARY_BAR'),
    TOP_LIST_SUMMARY_CARD: i18n.global.t('MESSAGE.UI.DASHBOARD.TOP_LIST_SUMMARY_CARD'),
    SERVICE_CARD_STYLE_1: i18n.global.t('MESSAGE.UI.DASHBOARD.BUSINESS_CARD_STYLE_1'),
    SERVICE_CARD_STYLE_2: i18n.global.t('MESSAGE.UI.DASHBOARD.BUSINESS_CARD_STYLE_2'),
    GAUGE_GAUGE_HALF_CIRCLE: i18n.global.t('MESSAGE.UI.DASHBOARD.GAUGE_HALF_CIRCLE'),
    GAUGE_GAUGE_CIRCLE: i18n.global.t('MESSAGE.UI.DASHBOARD.GAUGE_CIRCLE'),
    HEATMAP_CURRENT_HEATMAP: i18n.global.t('MESSAGE.UI.DASHBOARD.HEATMAP_CURRENT_HEATMAP'),
    CLUSTER_CLUSTER: i18n.global.t('MESSAGE.UI.DASHBOARD.CLUSTER_DISPLAY'),
    REALTIME_CARD_LIST: i18n.global.t('MESSAGE.UI.DASHBOARD.REALTIME_CARD_LIST'),
    REALTIME_SCOREBOARD: i18n.global.t('MESSAGE.UI.DASHBOARD.REALTIME_SCOREBOARD'),
    REALTIME_ALERT_TABLE: i18n.global.t('MESSAGE.UI.DASHBOARD.REALTIME_ALERT_TABLE'),
    REALTIME_SCOREBOARD_TABLE: i18n.global.t('MESSAGE.UI.DASHBOARD.REALTIME_SCOREBOARD_TABLE'),
    REALTIME_PIE: i18n.global.t('MESSAGE.UI.DASHBOARD.REALTIME_PIE'),
    BY_PERIOD_PIE: i18n.global.t('MESSAGE.UI.DASHBOARD.BY_PERIOD_PIE'),
    BY_PERIOD_LINE: i18n.global.t('MESSAGE.UI.DASHBOARD.BY_PERIOD_LINE'),
    BY_PERIOD_ALERT_TABLE: i18n.global.t('MESSAGE.UI.DASHBOARD.BY_PERIOD_ALERT_TABLE'),
  };
};

export const getDisplayStyleList = (): Record<
  WidgetChartType,
  { name: string; value: DisplayStyleType }[]
> => {
  return {
    TIME_SERIES: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.LINE'),
        value: DISPLAY_STYLE_KEY.LINE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.AREA'),
        value: DISPLAY_STYLE_KEY.AREA,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.BAR'),
        value: DISPLAY_STYLE_KEY.BAR,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STACKED_AREA'),
        value: DISPLAY_STYLE_KEY.STACKED_AREA,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STACKED_BAR'),
        value: DISPLAY_STYLE_KEY.STACKED_BAR,
      },
    ],
    SCATTER: [
      {
        name: i18n.global.t('WORD.DEFAULT'),
        value: DISPLAY_STYLE_KEY.SCATTER_BASIC,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.LONG_TERM'),
        value: DISPLAY_STYLE_KEY.SCATTER_LONG_TERM,
      },
    ],
    ONE_DAY_COMPARE: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.LINE'),
        value: DISPLAY_STYLE_KEY.LINE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.BAR'),
        value: DISPLAY_STYLE_KEY.BAR,
      },
    ],
    EQUALIZER: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.EQUALIZER_ENTRY'),
        value: DISPLAY_STYLE_KEY.EQUALIZER_ENTRY,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.EQUALIZER_GROUP'),
        value: DISPLAY_STYLE_KEY.EQUALIZER_GROUP,
      },
    ],
    SCOREBOARD: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_1'),
        value: DISPLAY_STYLE_KEY.STYLE_1,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_2'),
        value: DISPLAY_STYLE_KEY.STYLE_2,
      },
    ],
    PIE: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.PIE'),
        value: DISPLAY_STYLE_KEY.PIE,
      },
    ],
    STACKED_PROPORTION_BAR: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SINGLE_STACK'),
        value: DISPLAY_STYLE_KEY.SINGLE_STACK,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.MULTI_STACK'),
        value: DISPLAY_STYLE_KEY.MULTI_STACK,
      },
    ],
    GAUGE: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.GAUGE_CIRCLE'),
        value: DISPLAY_STYLE_KEY.GAUGE_CIRCLE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.GAUGE_HALF_CIRCLE'),
        value: DISPLAY_STYLE_KEY.GAUGE_HALF_CIRCLE,
      },
    ],
    ACTION_VIEW: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_1'),
        value: DISPLAY_STYLE_KEY.STYLE_1,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_2'),
        value: DISPLAY_STYLE_KEY.STYLE_2,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_3'),
        value: DISPLAY_STYLE_KEY.STYLE_3,
      },
    ],
    HEATMAP: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.CURRENT_HEATMAP'),
        value: DISPLAY_STYLE_KEY.CURRENT_HEATMAP,
      },
    ],
    CLUSTER: [
      {
        name: i18n.global.t('WORD.CLUSTER'),
        value: DISPLAY_STYLE_KEY.CLUSTER,
      },
    ],
    TABLE: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TABLE'),
        value: DISPLAY_STYLE_KEY.TABLE,
      },
    ],
    TREE_GRID: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TREE_GRID'),
        value: DISPLAY_STYLE_KEY.TREE_GRID,
      },
    ],
    TOP_LIST: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SUMMARY_BAR'),
        value: DISPLAY_STYLE_KEY.SUMMARY_BAR,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SUMMARY_CARD'),
        value: DISPLAY_STYLE_KEY.SUMMARY_CARD,
      },
    ],
    // CLUSTER_OVERVIEW: [],
    TOPOLOGY: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TOPOLOGY'),
        value: DISPLAY_STYLE_KEY.TOPOLOGY,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.POINT_MAP'),
        value: DISPLAY_STYLE_KEY.POINT_MAP,
      },
      // { name: 'WORD.UI.DASHBOARD.STYLE_2', value: DISPLAY_STYLE_KEY.STYLE_2 },
      // { name: 'WORD.UI.DASHBOARD.STYLE_3', value: DISPLAY_STYLE_KEY.STYLE_3 },
    ],
    EXCLUSIVE: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_1'),
        value: DISPLAY_STYLE_KEY.STYLE_1,
      },
      // { name: 'WORD.UI.DASHBOARD.STYLE_2', value: DISPLAY_STYLE_KEY.STYLE_2 },
      // { name: 'WORD.UI.DASHBOARD.STYLE_3', value: DISPLAY_STYLE_KEY.STYLE_3 },
    ],
    STATUS: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.HEXA'),
        value: DISPLAY_STYLE_KEY.HEXA,
      },
    ],
    STATUS_SUMMARY: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.CARD'),
        value: DISPLAY_STYLE_KEY.CARD,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.PIE'),
        value: DISPLAY_STYLE_KEY.PIE,
      },
    ],
    SERVICE_CARD: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_1'),
        value: DISPLAY_STYLE_KEY.STYLE_1,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.STYLE_2'),
        value: DISPLAY_STYLE_KEY.STYLE_2,
      },
    ],
    MEMO: [],
    TEXT: [],
    IMAGE: [],
    GROUP: [],
    TAB_GROUP: [],
    HORIZONTAL_FILTER: [
      { name: '가로형', value: DISPLAY_STYLE_KEY.HORIZONTAL },
      { name: '세로형', value: DISPLAY_STYLE_KEY.VERTICAL },
    ],
    VERTICAL_FILTER: [
      { name: '세로형', value: DISPLAY_STYLE_KEY.VERTICAL },
      { name: '가로형', value: DISPLAY_STYLE_KEY.HORIZONTAL },
    ],
    SERVER: [{ name: i18n.global.t('WORD.UI.DASHBOARD.SERVER'), value: DISPLAY_STYLE_KEY.SERVER }],
    REALTIME: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.CARD_LIST'),
        value: DISPLAY_STYLE_KEY.CARD_LIST,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SCOREBOARD'),
        value: DISPLAY_STYLE_KEY.SCOREBOARD,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TABLE'),
        value: DISPLAY_STYLE_KEY.ALERT_TABLE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.SCOREBOARD_TABLE'),
        value: DISPLAY_STYLE_KEY.SCOREBOARD_TABLE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.PIE'),
        value: DISPLAY_STYLE_KEY.PIE,
      },
    ],
    BY_PERIOD: [
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.PIE'),
        value: DISPLAY_STYLE_KEY.PIE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.LINE'),
        value: DISPLAY_STYLE_KEY.LINE,
      },
      {
        name: i18n.global.t('WORD.UI.DASHBOARD.TABLE'),
        value: DISPLAY_STYLE_KEY.ALERT_TABLE,
      },
    ],
    LEGEND: [],
  };
};

export const WIDGET_FONT_SIZE_LIST = [
  {
    name: '12',
    value: '12px',
  },
  {
    name: '15',
    value: '15px',
  },
  {
    name: '20',
    value: '20px',
  },
  {
    name: '25',
    value: '25px',
  },
];

export const WIDGET_TEXT_ALIGN_LIST: {
  name: string;
  value: TextAlignType;
  iconClass: IconType;
}[] = [
  {
    name: 'Left align',
    value: 'left',
    iconClass: 'icon-outline-align-left',
  },
  {
    name: 'Center align',
    value: 'center',
    iconClass: 'icon-outline-align-center',
  },
  {
    name: 'Right align',
    value: 'right',
    iconClass: 'icon-outline-align-right',
  },
];

// 영문자 기준으로 작성
export const MAX_LENGTH_FOR_DASHBOARD: Record<LENGTH_RULE_KEY, number> = {
  NAME: 150,
  DESCRIPTION: 300,
  WIDGET_NAME: 60,
  TEXT_WIDGET: 1024,
};

export const ALL_KEY = 'ALL';
export const CUSTOM_KEY = 'CUSTOM';
export const ASTER_KEY = '*';

export const MAX_CHART_DATA_COUNT = 26;
export const MAX_FORMULA_DATA_COUNT = 10;
export const MAX_THRESHOLDS_DATA_COUNT = 5;

export const BY_TARGET_CHART_TYPES: Partial<WidgetChartType>[] = [
  'SCATTER',
  'EQUALIZER',
  'ACTION_VIEW',
  'EXCLUSIVE',
  'STATUS',
  'STATUS_SUMMARY',
  'TABLE',
  'SERVICE_CARD',
  'TOP_LIST',
  'PIE',
  'TREE_GRID',
  'HEATMAP',
];

export const CHART_TYPES_WITH_VALUE_ISSHOW: WidgetChartType[] = [
  'TIME_SERIES',
  'SCATTER',
  'SCOREBOARD',
  'STACKED_PROPORTION_BAR',
  'ACTION_VIEW',
];

export const BY_STAT_CHART_TYPES = [
  { chartType: 'ONE_DAY_COMPARE', displayStyle: DISPLAY_STYLE_KEY.LINE },
  { chartType: 'ONE_DAY_COMPARE', displayStyle: DISPLAY_STYLE_KEY.BAR },
  { chartType: 'SCOREBOARD', displayStyle: DISPLAY_STYLE_KEY.STYLE_2 },
] as const;

export const DATA_TYPE_LIST: { name: string; value: WidgetDataType }[] = [
  {
    name: 'Last',
    value: 'current',
  },
  {
    name: 'Max',
    value: 'max',
  },
  {
    name: 'Sum',
    value: 'sum',
  },
  {
    name: 'Avg',
    value: 'avg',
  },
  {
    name: 'Delta',
    value: 'delta',
  },
  {
    name: 'Delta(per Sec)',
    value: 'deltaPerSec',
  },
  {
    name: 'Sum(per Sec)',
    value: 'sumPerSec',
  },
];

export const SERIES_TYPES_FOR_STAT: { name: string; value: WidgetSeriesType }[] = [
  {
    name: 'Avg',
    value: 'avg',
  },
  {
    name: 'Max',
    value: 'max',
  },
  {
    name: 'Sum',
    value: 'sum',
  },
];

export const SERIES_TYPES_FOR_BY_TARGET: { name: string; value: WidgetSeriesType }[] = [
  {
    name: 'By',
    value: BY_TARGET_SERIES_TYPE,
  },
];

export const CLOUD_DB_RESOURCE_TARGET_TYPES: { name: string; value: TargetType }[] = [
  {
    name: 'AWS DMS Replication Instance',
    value: 'aws_dms_replinstance',
  },
  {
    name: 'AWS DMS Task',
    value: 'aws_dms_task',
  },
  {
    name: 'AWS DocumentDB',
    value: 'aws_documentdb',
  },
  {
    name: 'AWS DynamoDB',
    value: 'aws_dynamodb',
  },
  {
    name: 'AWS EC2',
    value: 'aws_ec2',
  },
  // {
  //   name: 'AWS ECS Cluster',
  //   value: 'aws_ecs_cluster',
  // },
  // {
  //   name: 'AWS ECS Service',
  //   value: 'aws_ecs_service',
  // },
  {
    name: 'AWS ElastiCache Cluster',
    value: 'aws_elasticache_cluster',
  },
  {
    name: 'AWS ElastiCache Shard',
    value: 'aws_elasticache_shard',
  },
  {
    name: 'AWS ElastiCache Node',
    value: 'aws_elasticache_node',
  },
  {
    name: 'AWS ElastiCache Serverless',
    value: 'aws_elasticache_serverless',
  },
  {
    name: 'AWS RDS',
    value: 'aws_rds',
  },
  {
    name: 'AWS Redshift',
    value: 'aws_redshift',
  },
  {
    name: 'Azure Virtual Machines',
    value: 'azure_virtual_machines',
  },
  {
    name: 'Azure Functions',
    value: 'azure_functions',
  },
  // {
  //   name: 'Azure Blob Storage',
  //   value: 'azure_blob_storage',
  // },
  {
    name: 'Azure Cosmos DB',
    value: 'azure_cosmos_db',
  },
  {
    name: 'Azure Cache for Redis',
    value: 'azure_cache_for_redis',
  },
  {
    name: 'Azure Cache for Redis Enterprise',
    value: 'azure_cache_for_redis_enterprise',
  },
];

export const TARGET_TYPES: { name: string; value: TargetType }[] = [
  {
    name: 'Node',
    value: 'node',
  },
  {
    name: 'Pod',
    value: 'pod',
  },
  {
    name: 'Container',
    value: 'container',
  },
  {
    name: 'Application',
    value: 'application',
  },
  {
    name: 'Database',
    value: 'database',
  },
  {
    name: 'Host',
    value: 'host',
  },
  {
    name: 'GPU',
    value: 'gpu',
  },
  {
    name: 'Network Device',
    value: 'network_device',
  },
  ...CLOUD_DB_RESOURCE_TARGET_TYPES,
  {
    name: 'Kafka Broker',
    value: 'kafka_broker',
  },
  {
    name: 'Kafka Cluster Consumer Group',
    value: 'kafka_cluster_consumer_group',
  },
];

export const MAXGAUGE_TARGET_TYPES: { name: string; value: TargetType }[] = [
  {
    name: 'Database',
    value: 'database',
  },
  {
    name: 'Host',
    value: 'host',
  },
  {
    name: 'GPU',
    value: 'gpu',
  },
  ...CLOUD_DB_RESOURCE_TARGET_TYPES,
];

export const SQL_INFO_DETAIL_COMPONENTS = {
  common: defineAsyncComponent(
    () => import('@/database/components/sqlDetail/InstanceSqlDetailWithParams.vue'),
  ),
  [QUERY_TYPE.SLOW_QUERY]: defineAsyncComponent(
    () => import('@/database/components/slowQuery/slowQuerySqlDetail/SlowQuerySqlDetail.vue'),
  ),
};

export const SUMMARY_DETAIL_INFO: Record<string, { type: DetailType; params: DetailParam[] }> = {
  // 트랜잭션 패널 정보 파라미터와 통일하기 위해 파라미터 형식 변경
  etoeTrace: {
    type: TOTAL_DETAIL.TRANSACTION,
    params: [
      {
        field: 'wasId',
        dataType: 'string',
      },
      {
        field: 'tid',
        dataType: 'string',
      },
      {
        field: 'collectTime',
        dataType: 'string',
      },
    ],
  },
};

export const FIXED_DATA_ID = {
  STATUS: 'target_status_list',
  STATUS_SUMMARY: 'target_status_summary',
  TOPOLOGY: 'target_topology',
  SERVER: 'target_status_list',
  CLUSTER: 'target_cluster',
  REALTIME: 'target_alert_list',
  ALERT_COUNT: 'target_alert_count',
  ALERT_HISTORY_LIST: 'target_alert_history_list',
  ALERT_HISTORY_COUNT: 'target_alert_history_count',
  ALERT_METRIC: 'target_alert_metric',
} as const;

export const FIXED_DATA_ID_BY_CHART_TYPE: Record<
  Extract<
    WidgetChartType,
    'SERVER' | 'STATUS' | 'STATUS_SUMMARY' | 'TOPOLOGY' | 'CLUSTER' | 'REALTIME'
  >,
  (typeof FIXED_DATA_ID)[keyof typeof FIXED_DATA_ID]
> = {
  SERVER: FIXED_DATA_ID.SERVER,
  STATUS: FIXED_DATA_ID.STATUS,
  STATUS_SUMMARY: FIXED_DATA_ID.STATUS_SUMMARY,
  TOPOLOGY: FIXED_DATA_ID.TOPOLOGY,
  CLUSTER: FIXED_DATA_ID.CLUSTER,
  REALTIME: FIXED_DATA_ID.REALTIME,
};

export const TIME_RANGE_COUNT_BY_CHART_TYPE = {
  TABLE: 2,
  TREE_GRID: 2,
};

export const TimeArgumentList = ['fromTime', 'toTime'] as const;
export type TimeArgumentList = (typeof TimeArgumentList)[number];

export const WidgetCommonArgument: Record<WidgetChartType, Readonly<string[]> | undefined> = {
  TIME_SERIES: TimeArgumentList,
  SCATTER: undefined,
  ONE_DAY_COMPARE: undefined,
  EQUALIZER: undefined,
  SCOREBOARD: undefined,
  STACKED_PROPORTION_BAR: undefined,
  GAUGE: undefined,
  ACTION_VIEW: undefined,
  HEATMAP: undefined,
  TREE_GRID: ['clickedTime'],
  TABLE: undefined,
  TOP_LIST: undefined,
  EXCLUSIVE: undefined,
  STATUS: undefined,
  STATUS_SUMMARY: undefined,
  SERVICE_CARD: undefined,
  MEMO: undefined,
  TEXT: undefined,
  IMAGE: undefined,
  GROUP: undefined,
  TAB_GROUP: undefined,
  PIE: undefined,
  TOPOLOGY: undefined,
  HORIZONTAL_FILTER: undefined,
  VERTICAL_FILTER: undefined,
  SERVER: undefined,
  LEGEND: undefined,
  CLUSTER: undefined,
  REALTIME: undefined,
  BY_PERIOD: undefined,
} as const;

export const TIME_PERIOD_LIST_FOR_TOOLTIP = () => [
  {
    name: i18n.global.t('WORD.GLOBAL_TIME'),
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.GLOBAL_TIME'),
  },
  {
    name: `${i18n.global.t('WORD.TIME_PERIOD_ITEMS.PAST_5_MINUTES')}, ${i18n.global.t(
      'WORD.TIME_PERIOD_ITEMS.PAST_10_MINUTES',
    )}`,
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.P5M_P10M'),
  },
  {
    name: `${i18n.global.t('WORD.TIME_PERIOD_ITEMS.PAST_30_MINUTES')}, ${i18n.global.t(
      'WORD.TIME_PERIOD_ITEMS.PAST_1_HOURS',
    )}`,
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.P30M_P1H'),
  },
  {
    name: `${i18n.global.t('WORD.TIME_PERIOD_ITEMS.PAST_3_HOURS')}, ${i18n.global.t(
      'WORD.TIME_PERIOD_ITEMS.PAST_6_HOURS',
    )}, ${i18n.global.t('WORD.TIME_PERIOD_ITEMS.PAST_12_HOURS')}, ${i18n.global.t(
      'WORD.TIME_PERIOD_ITEMS.PAST_1_DAY',
    )}`,
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.P3H_P6H_P12H_P1D'),
  },
  {
    name: i18n.global.t('WORD.TIME_PERIOD_ITEMS.PAST_7_DAY'),
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.P7D'),
  },
  {
    name: i18n.global.t('WORD.TIME_PERIOD_ITEMS.OVER_7_DAY'),
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.OVER_P7D'),
  },
  {
    name: i18n.global.t('WORD.TIME_PERIOD_ITEMS.SELECT_TIME_RANGE'),
    value: i18n.global.t('MESSAGE.UI.DASHBOARD_WIDGET_TIME_PERIOD_INFO.SELECT_TIME_RANGE'),
  },
];

export const ABORT_API_KEYS = {
  STAT_DETAIL: 'statDetail',
  TARGET_COLUMN: 'targetColumn',
};
